/*
eslint-disable
react/jsx-filename-extension,
react/jsx-props-no-spreading,
react/static-property-placement
*/

import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import ContentArticleList from './content-article-list';
import './article-list-infinite.scss';

/**
 * A React component for fetching articles for an article list, and (in part) an
 * analogue to the article-list PHP template.
 */
export default class ArticleListInfinite extends Component {
  /**
   * Most of these props mimic the variables passed to the PHP article-list
   * template. Some of the props are also available in the article list content
   * template, but they're passed to this component first, and then on to the
   * content template, to maintain parity of logic with the server templates.
   */
  static propTypes = {
    listId: PropTypes.string.isRequired,
    context: PropTypes.string.isRequired,
    h: PropTypes.string.isRequired,
    initialLength: PropTypes.string.isRequired,
    contentClasses: PropTypes.string.isRequired,
    articleClasses: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    loadingText: PropTypes.string.isRequired,
    errorText: PropTypes.string.isRequired,
    next: PropTypes.string.isRequired,
    siteModel: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    showThumb: PropTypes.string.isRequired,
    default_ap_image: PropTypes.shape({
      orientation: PropTypes.string,
      src: PropTypes.string,
      alt_text: PropTypes.string,
    }).isRequired,
  };

  /**
   * This component is usually generated by the reactComponentManager, so
   * passing the global site model object won't be feasible.
   */
  static defaultProps = {
    siteModel: window.nexstar,
  };

  /**
   * Start the component.
   */
  constructor(props) {
    super(props);

    this.state = {
      articles: [],
      loading: false,
      error: false,
      nextUrl: new URL(props.next),
      gridHeight: 0,
      displayedArticles: 0,
    };
    this.isMobile = matchMedia('(max-width: 900px)').matches;
    this.displayedArticleSize = 7;
    this.toBeIncreasedGridHeight = this.isMobile ? 1145 : 550;
    this.loadNextPage = this.loadNextPage.bind(this);
  }

  /**
   * Set the component to a state with an error message and no URL for new articles.
   */
  setErrorState() {
    this.setState({
      nextUrl: false,
      error: true,
    });
  }

  /**
   * Given a URL, a target length of articles, and a maximum number of
   * iterations, recursively runs itself until either the number of articles
   * specified are fetched or the max iterations are reached.
   *
   * @param {array} accumulatedArticles The accumulated articles so far.
   * @param {number} targetArticles The number of articles that must be reached by this fetch in order to terminate execution.
   * @param {number} allowedIterations The remaining maximum iterations to run before stopping.
   * @param {URL|boolean} UrlToFetch The URL to fetch on this iteration.
   */
  getNextIteration(
    accumulatedArticles,
    targetArticles,
    allowedIterations,
    UrlToFetch,
  ) {
    if (
      accumulatedArticles.length >= targetArticles
      || 0 >= allowedIterations
      || ! UrlToFetch
    ) {
      const out = new $.Deferred();

      out.resolve({
        accumulatedArticles,
        UrlToFetch,
      });

      return out.promise();
    }

    return fetch(`${UrlToFetch.href}&truncate_title=1`)
      .then((response) => {
        if (! response.ok) {
          throw new Error('Network response not ok');
        }
        return response.json();
      })
      .then((response) => {
        const {
          _embedded: {
            // The entire embedded post responses.
            articles: embeddedArticles = null,
          } = {},
          _links: {
            next = [],
          } = {},
        } = response;

        // The URL to the next page, if any, will be in the first 'next' link object.
        const nextHref = next[0] && next[0].href ? next[0].href : '';

        // no/no more articles available
        if (! embeddedArticles) {
          const out = new $.Deferred();
          out.resolve({
            accumulatedArticles,
            UrlToFetch: false,
          });
          return out.promise();
        }

        /**
         * Out of the entire embedded post response, extract the variables for
         * the article list template. Append them to the list.
         */
        accumulatedArticles.push(
          ...embeddedArticles.map((embed) => ({
            ...embed.article_list,
            title: embed.title.rendered,
            default_ap_image: embed.default_ap_image,
          })),
        );

        return this.getNextIteration(
          accumulatedArticles,
          targetArticles,
          allowedIterations - 1,
          nextHref ? new URL(nextHref) : false,
        );
      })
      .catch(() => {
        this.setErrorState();
      });
  }

  /**
   * Callback to load the next page of articles.
   */
  loadNextPage() {
    const {
      initialLength,
    } = this.props;

    const {
      articles: currentArticles,
      loading,
      nextUrl,
      displayedArticles,
    } = this.state;

    const {
      context,
    } = this.props;

    const isFourColumn = context?.includes('four_col');

    // only increase the grid height if there are already enough fetched items
    // to render (no need to refetch before rendering new rows)
    if (
      isFourColumn
      && this.displayedArticleSize <= currentArticles.length - displayedArticles
    ) {
      this.setState((prevState) => ({
        gridHeight: prevState.gridHeight + this.toBeIncreasedGridHeight,
        displayedArticles:
          prevState.displayedArticles + this.displayedArticleSize,
      }));
      return;
    }

    if (! nextUrl || ! nextUrl.href || loading) {
      if (isFourColumn) {
        if (displayedArticles > currentArticles.length) {
          return;
        }
        this.setState((prevState) => ({
          gridHeight: prevState.gridHeight + this.toBeIncreasedGridHeight,
          displayedArticles:
            prevState.displayedArticles + this.displayedArticleSize,
        }));
      }
      return;
    }

    this.setState({
      loading: true,
    });

    // The articles to load are embedded resources, so make sure those are requested.
    nextUrl.searchParams.delete('_embed');
    nextUrl.searchParams.append('_embed', '1');

    /**
     * The number of articles we aim to load dynamically is usually equal to the
     * number of articles visible in the list on load from the server. This
     * number might require loading multiple pages' worth of articles to reach.
     * All current use cases should be achievable in two fetches, but increase
     * maxIterations if necessary for new use cases.
     *
     * Note: Increased to 6 iterations for NNN section front top stories, which
     * only has 1 ranked article in the hero (see NNN-32).
     */
    const targetLength = Number(initialLength);
    const maxIterations = targetLength ? 6 : 1;

    try {
      this.getNextIteration(
        [],
        targetLength,
        maxIterations,
        nextUrl,
      ).then(({ accumulatedArticles, UrlToFetch }) => {
        if (isFourColumn) {
          Promise.resolve(
            this.setState({
              articles: [...currentArticles, ...accumulatedArticles],
              nextUrl: UrlToFetch,
              loading: false,
            }),
          ).then(() => this.setState((prevState) => ({
            gridHeight: prevState.gridHeight + this.toBeIncreasedGridHeight,
            displayedArticles:
              prevState.displayedArticles + this.displayedArticleSize,
          })));
        } else {
          this.setState({
            articles: [...currentArticles, ...accumulatedArticles],
            nextUrl: UrlToFetch,
            loading: false,
          });
        }
      });
    } catch (e) {
      this.setErrorState();
    }
  }

  render() {
    const {
      listId,
      context,
      contentClasses,
      articleClasses,
      loadingText,
      errorText,
      siteModel,
      showThumb,
      initialLength,
      next,
    } = this.props;

    /**
     * This is a string because it can come from the data attribute, but it
     * needs to be a number to perform addition for the heading level.
     */
    let {
      h,
    } = this.props;

    const {
      articles,
      loading,
      error,
      nextUrl,
      gridHeight,
    } = this.state;

    h = Number(h);
    let showImage = 'true' === showThumb;
    const nextParams = new URLSearchParams(next);
    const nextArticlesLength = nextParams?.get('per_page') ?? 0;
    const useFixedHeight = parseInt(nextArticlesLength, 10) > parseInt(initialLength, 10); // eslint-disable-line max-len
    const isFourColumn = context?.includes('four_col');
    const isNumbered = context?.includes('numbered_list');
    const hasFeatured = context?.includes('featured') || 'headline_list--is_infinite' === context; // eslint-disable-line max-len
    let auxContext = context;

    let articleComponents = articles.map((article) => {
      const articleClassesCombined = articleClasses.split(' ');
      let defaultApImage = article?.default_ap_image; // eslint-disable-line camelcase

      if ('headline_list--is_infinite' === context) {
        auxContext = 'headline_list_text_only--is_infinite';
      }

      if (
        'headline_list_text_only--is_infinite' === auxContext
        || 'headline_list_two_col--is_infinite' === auxContext
        || 'headline_list_three_col--is_infinite' === auxContext
      ) {
        showImage = false;
        defaultApImage = false;
      }

      // Article has a featured image.
      if (showImage) {
        articleClassesCombined.push('article-list__article--has-thumb');
      }

      // Article has lead media.
      if (showImage && article.lead_media && article.lead_media.type) {
        // @todo update this class name -- media type is also something else
        articleClassesCombined.push('article-list__article--is-media-type');
      }

      try {
        return (
          <article
            key={article.id}
            className={articleClassesCombined.join(' ')}
          >
            <ContentArticleList
              key={article.id}
              {...article}
              listId={listId}
              context={auxContext}
              h={article.title ? h + 1 : h}
              site_model={siteModel}
              showThumb={showImage}
              default_ap_image={defaultApImage}
            />
          </article>
        );
      } catch (e) {
        return null;
      }
    });
    articleComponents = articleComponents.filter(Boolean);

    let buttonText = 'Show More';
    let additionalClass = '';

    const style = {
      height: isFourColumn && useFixedHeight ? gridHeight : 'auto',
      counterReset: isNumbered ? `trending ${initialLength}` : 'trending',
    };

    if (isNumbered) {
      additionalClass = 'numbered-continuation';
    }

    if (error) {
      buttonText = errorText;
    } else if (loading) {
      buttonText = loadingText;
    }

    /* eslint-disable react/button-has-type */
    return (
      <>
        {1 <= articles.length
          && (
            <div
              className={`${contentClasses} ${additionalClass}`}
              style={style}
            >
              {hasFeatured && (
                <article
                  className="article-list__article"
                  style={{
                    height: '0',
                    margin: '0',
                    display: 'none',
                  }}
                />
              )}
              {articleComponents}
            </div>
          )}

        {(nextUrl)
          && (
            <div className="article-list__load-more-cta">
              <button
                className="article-list__load-more-cta__button"
                disabled={loading || error}
                onClick={this.loadNextPage}
                rel="next"
              >
                {buttonText}
              </button>
            </div>
          )}
      </>
    );
    /* eslint-enable react/button-has-type */
  }
}
